import { useMemo } from 'react'
import { Input } from 'app/components/ui/input'
import { extractTokenKeys, TEMPLATE_TOKENS } from 'app/lib/messageTemplates'

type TemplateFallbacksEditorProps = {
  /** The tokened text(s) to scan, pass body, or title + "\n" + description. */
  tokenedText: string
  value: Record<string, string>
  onChange: (next: Record<string, string>) => void
  /** Label for a token key. Defaults to the shared TEMPLATE_TOKENS catalog, which covers contact
   *  fields. Surfaces with their own vocabulary (the AI agent's `activity.*` / `agent.*` tokens) pass
   *  their own resolver so a row reads "Contact name" rather than "activity.contactName". */
  labelFor?: (key: string) => string
}

/**
 * Prepare a fallbacks map for saving: keep only non-empty values whose token
 * is still present in the template text, so editing a token out of the body
 * doesn't leave an invisible orphan entry behind. Returns undefined when
 * nothing survives (the stores treat that as "clear").
 */
export function pruneFallbacks(
  fallbacks: Record<string, string>,
  tokenedText: string,
): Record<string, string> | undefined {
  const usedKeys = new Set(extractTokenKeys(tokenedText))
  const entries = Object.entries(fallbacks)
    .map(([key, text]) => [key, text.trim()] as const)
    .filter(([key, text]) => text !== `` && usedKeys.has(key))
  return entries.length ? Object.fromEntries(entries) : undefined
}

/**
 * Per-token fallback inputs, shown only for tokens actually used in the
 * template (mirrors the bulk Send-SMS modal's proven pattern). Empty input
 * = no fallback for that token.
 */
export function TemplateFallbacksEditor({ tokenedText, value, onChange, labelFor }: TemplateFallbacksEditorProps) {
  const usedKeys = useMemo(() => [...new Set(extractTokenKeys(tokenedText))], [tokenedText])
  if (usedKeys.length === 0) return null

  const label = labelFor ?? ((key: string) => TEMPLATE_TOKENS.find((t) => t.key === key)?.label ?? key)

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-ui-sm font-medium text-default">
        Fallbacks <span className="font-normal text-minimal">(used when a contact is missing the value)</span>
      </span>
      {usedKeys.map((key) => (
        <div key={key} className="flex items-center gap-2">
          <span className="w-28 shrink-0 text-xs text-subtle">{label(key)}</span>
          <Input
            value={value[key] ?? ``}
            placeholder="e.g. there"
            size="sm"
            onChange={(e) => onChange({ ...value, [key]: e.target.value })}
          />
        </div>
      ))}
    </div>
  )
}
